"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { BarChart3, Building2, CreditCard, Fingerprint, KeyRound, LayoutDashboard, LogOut, Megaphone, ShieldCheck, Tag, Users, Waypoints } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth-client";
import { useEdition } from "@/lib/edition";
import { Logo } from "./ui/logo";
import type { ReactNode } from "react";
interface AdminShellProps {
  children: ReactNode;
}
export function AdminShell(t0) {
  const $ = _c(88);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    edition,
    selfServeBilling
  } = useEdition();
  const isDashboard = pathname === "/" || pathname === "";
  const isTokens = pathname === "/tokens";
  const isUsers = pathname === "/users";
  const isTransactions = pathname === "/transactions";
  const isMarketing = pathname === "/marketing";
  const isCoupons = pathname === "/coupons";
  const isSubscriptionPlans = pathname === "/subscription-plans";
  const isSubscriptions = pathname === "/subscriptions";
  const isUsage = pathname === "/usage";
  const isSmartRouter = pathname === "/smart-router";
  const isGuardrails = pathname === "/guardrails";
  const isEnterprise = pathname === "/enterprise";
  let t1;
  if ($[0] !== queryClient || $[1] !== router || $[2] !== signOut) {
    t1 = async () => {
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push("/login");
          }
        }
      });
    };
    $[0] = queryClient;
    $[1] = router;
    $[2] = signOut;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSignOut = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Logo className="h-8 w-8" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-sm font-semibold leading-tight">RouterBench</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = edition === "enterprise" ? "Enterprise" : "B2C";
  let t5;
  if ($[6] !== t4) {
    t5 = <SidebarHeader className="border-b border-sidebar-border/60"><div className="flex h-12 items-center justify-between px-2"><div className="flex items-center gap-2 px-1">{t2}<div className="flex flex-col">{t3}<span className="text-[11px] font-medium uppercase tracking-wide text-primary">Admin · {t4}</span></div></div></div></SidebarHeader>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SidebarGroupLabel>Overview</SidebarGroupLabel>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <LayoutDashboard className="h-4 w-4" />;
    t8 = <span>Dashboard</span>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isDashboard) {
    t9 = <SidebarMenuItem><Link href="/" className="block"><SidebarMenuButton isActive={isDashboard} size="lg">{t7}{t8}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[11] = isDashboard;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <BarChart3 className="h-4 w-4" />;
    t11 = <span>Usage Analytics</span>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isUsage) {
    t12 = <SidebarMenuItem><Link href="/usage" className="block"><SidebarMenuButton isActive={isUsage} size="lg">{t10}{t11}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[15] = isUsage;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <KeyRound className="h-4 w-4" />;
    t14 = <span>Token Usage</span>;
    $[17] = t13;
    $[18] = t14;
  } else {
    t13 = $[17];
    t14 = $[18];
  }
  let t15;
  if ($[19] !== isTokens) {
    t15 = <SidebarMenuItem><Link href="/tokens" className="block"><SidebarMenuButton isActive={isTokens} size="lg">{t13}{t14}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[19] = isTokens;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t12 || $[22] !== t15 || $[23] !== t9) {
    t16 = <SidebarGroup>{t6}<SidebarMenu>{t9}{t12}{t15}</SidebarMenu></SidebarGroup>;
    $[21] = t12;
    $[22] = t15;
    $[23] = t9;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SidebarGroupLabel>Customers</SidebarGroupLabel>;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Users className="h-4 w-4" />;
    t19 = <span>{"Users & Credits"}</span>;
    $[26] = t18;
    $[27] = t19;
  } else {
    t18 = $[26];
    t19 = $[27];
  }
  let t20;
  if ($[28] !== isUsers) {
    t20 = <SidebarGroup>{t17}<SidebarMenu><SidebarMenuItem><Link href="/users" className="block"><SidebarMenuButton isActive={isUsers} size="lg">{t18}{t19}</SidebarMenuButton></Link></SidebarMenuItem></SidebarMenu></SidebarGroup>;
    $[28] = isUsers;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SidebarGroupLabel>Billing</SidebarGroupLabel>;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  let t23;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ShieldCheck className="h-4 w-4" />;
    t23 = <span>Plans</span>;
    $[31] = t22;
    $[32] = t23;
  } else {
    t22 = $[31];
    t23 = $[32];
  }
  let t24;
  if ($[33] !== isSubscriptionPlans) {
    t24 = <SidebarMenuItem><Link href="/subscription-plans" className="block"><SidebarMenuButton isActive={isSubscriptionPlans} size="lg">{t22}{t23}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[33] = isSubscriptionPlans;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  let t26;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Building2 className="h-4 w-4" />;
    t26 = <span>Subscriptions</span>;
    $[35] = t25;
    $[36] = t26;
  } else {
    t25 = $[35];
    t26 = $[36];
  }
  let t27;
  if ($[37] !== isSubscriptions) {
    t27 = <SidebarMenuItem><Link href="/subscriptions" className="block"><SidebarMenuButton isActive={isSubscriptions} size="lg">{t25}{t26}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[37] = isSubscriptions;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== isCoupons || $[40] !== isTransactions || $[41] !== selfServeBilling) {
    t28 = selfServeBilling ? <><SidebarMenuItem><Link href="/transactions" className="block"><SidebarMenuButton isActive={isTransactions} size="lg"><CreditCard className="h-4 w-4" /><span>Transactions</span></SidebarMenuButton></Link></SidebarMenuItem><SidebarMenuItem><Link href="/coupons" className="block"><SidebarMenuButton isActive={isCoupons} size="lg"><Tag className="h-4 w-4" /><span>Coupons</span></SidebarMenuButton></Link></SidebarMenuItem></> : null;
    $[39] = isCoupons;
    $[40] = isTransactions;
    $[41] = selfServeBilling;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== t24 || $[44] !== t27 || $[45] !== t28) {
    t29 = <SidebarGroup>{t21}<SidebarMenu>{t24}{t27}{t28}</SidebarMenu></SidebarGroup>;
    $[43] = t24;
    $[44] = t27;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <SidebarGroupLabel>Platform</SidebarGroupLabel>;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  let t32;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Waypoints className="h-4 w-4" />;
    t32 = <span>Smart Router</span>;
    $[48] = t31;
    $[49] = t32;
  } else {
    t31 = $[48];
    t32 = $[49];
  }
  let t33;
  if ($[50] !== isSmartRouter) {
    t33 = <SidebarMenuItem><Link href="/smart-router" className="block"><SidebarMenuButton isActive={isSmartRouter} size="lg">{t31}{t32}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[50] = isSmartRouter;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  let t35;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <ShieldCheck className="h-4 w-4" />;
    t35 = <span>Guardrails</span>;
    $[52] = t34;
    $[53] = t35;
  } else {
    t34 = $[52];
    t35 = $[53];
  }
  let t36;
  if ($[54] !== isGuardrails) {
    t36 = <SidebarMenuItem><Link href="/guardrails" className="block"><SidebarMenuButton isActive={isGuardrails} size="lg">{t34}{t35}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[54] = isGuardrails;
    $[55] = t36;
  } else {
    t36 = $[55];
  }
  let t37;
  let t38;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Fingerprint className="h-4 w-4" />;
    t38 = <span>Enterprise SSO</span>;
    $[56] = t37;
    $[57] = t38;
  } else {
    t37 = $[56];
    t38 = $[57];
  }
  let t39;
  if ($[58] !== isEnterprise) {
    t39 = <SidebarMenuItem><Link href="/enterprise" className="block"><SidebarMenuButton isActive={isEnterprise} size="lg">{t37}{t38}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[58] = isEnterprise;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  let t41;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Megaphone className="h-4 w-4" />;
    t41 = <span>Marketing</span>;
    $[60] = t40;
    $[61] = t41;
  } else {
    t40 = $[60];
    t41 = $[61];
  }
  let t42;
  if ($[62] !== isMarketing) {
    t42 = <SidebarMenuItem><Link href="/marketing" className="block"><SidebarMenuButton isActive={isMarketing} size="lg">{t40}{t41}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[62] = isMarketing;
    $[63] = t42;
  } else {
    t42 = $[63];
  }
  let t43;
  if ($[64] !== t33 || $[65] !== t36 || $[66] !== t39 || $[67] !== t42) {
    t43 = <SidebarGroup>{t30}<SidebarMenu>{t33}{t36}{t39}{t42}</SidebarMenu></SidebarGroup>;
    $[64] = t33;
    $[65] = t36;
    $[66] = t39;
    $[67] = t42;
    $[68] = t43;
  } else {
    t43 = $[68];
  }
  let t44;
  if ($[69] !== t16 || $[70] !== t20 || $[71] !== t29 || $[72] !== t43) {
    t44 = <SidebarContent>{t16}{t20}{t29}{t43}</SidebarContent>;
    $[69] = t16;
    $[70] = t20;
    $[71] = t29;
    $[72] = t43;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  let t46;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <LogOut className="h-3.5 w-3.5" />;
    t46 = <span>Sign out</span>;
    $[74] = t45;
    $[75] = t46;
  } else {
    t45 = $[74];
    t46 = $[75];
  }
  let t47;
  if ($[76] !== handleSignOut) {
    t47 = <SidebarFooter className="border-t border-sidebar-border/60"><Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs text-muted-foreground" onClick={handleSignOut}>{t45}{t46}</Button></SidebarFooter>;
    $[76] = handleSignOut;
    $[77] = t47;
  } else {
    t47 = $[77];
  }
  let t48;
  if ($[78] !== t44 || $[79] !== t47 || $[80] !== t5) {
    t48 = <Sidebar variant="inset">{t5}{t44}{t47}</Sidebar>;
    $[78] = t44;
    $[79] = t47;
    $[80] = t5;
    $[81] = t48;
  } else {
    t48 = $[81];
  }
  let t49;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="flex h-12 items-center border-b border-sidebar-border/60 px-4"><SidebarTrigger /></div>;
    $[82] = t49;
  } else {
    t49 = $[82];
  }
  let t50;
  if ($[83] !== children) {
    t50 = <SidebarInset>{t49}{children}</SidebarInset>;
    $[83] = children;
    $[84] = t50;
  } else {
    t50 = $[84];
  }
  let t51;
  if ($[85] !== t48 || $[86] !== t50) {
    t51 = <SidebarProvider>{t48}{t50}</SidebarProvider>;
    $[85] = t48;
    $[86] = t50;
    $[87] = t51;
  } else {
    t51 = $[87];
  }
  return t51;
}